import { useState, useEffect, useRef } from 'react'
import { useSearchParams,useLocation, useNavigate  } from 'react-router-dom'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr' 
import { 
  Search, Calendar, Clock, User, Phone, Mail, RefreshCw ,
  CheckCircle, XCircle, AlertCircle, MoreVertical,
  List as ListIcon, Calendar as CalendarIcon, Video, Building, Home, MapPin, X, Send, FileText, 
  ExternalLink, UserPlus, Save, Check, Search as SearchIcon, StickyNote, AlertTriangle, Loader2, CreditCard,
    Trash2, MessageSquare, PhoneCall, Copy
} from 'lucide-react' 
import toast from 'react-hot-toast'
import api from '../../services/api'

// --- CONFIGURATION ---

const STATUS_CONFIG = {
  confirmed: { label: 'Confirmé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: XCircle },
  completed: { label: 'Terminé', color: 'bg-gray-100 text-gray-700', icon: CheckCircle },
  no_show: { label: 'Absent', color: 'bg-orange-100 text-orange-700', icon: XCircle },
}

const CONSULTATION_TYPES = {
  cabinet: { label: 'Cabinet', icon: Building, color: '#7d9668', bg: '#e8f0e3' },
  video: { label: 'Vidéo', icon: Video, color: '#9333ea', bg: '#f3e8ff' },
  domicile: { label: 'Domicile', icon: Home, color: '#ea580c', bg: '#ffedd5' },
  telephone: { label: 'Téléphone', icon: PhoneCall, color: '#0d9488', bg: '#ccfbf1' },
}

export default function AppointmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  // DÉTECTION DU MODE : Est-ce qu'on vient de la page Patient ?
  const hasPendingPatient = !!location.state?.targetPatient

  // DÉTECTION DU FILTRE : Est-ce qu'on demande d'ouvrir un RDV spécifique ?
  // Si oui -> on force le filtre 'all' pour être sûr de le trouver (même s'il est passé)
  const initialFilter = searchParams.get('apt_id') ? 'all' : 'week'

  // INITIALISATION INTELLIGENTE :
  // Si on a un patient en attente -> Calendrier
  // Sinon -> Liste (comme avant)
  const [viewMode, setViewMode] = useState(hasPendingPatient ? 'calendar' : 'list')

  const [appointments, setAppointments] = useState([])
  const[tasks, setTasks] = useState([]) 
  const [calendarEvents, setCalendarEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [pendingPatient, setPendingPatient] = useState(null)
  const [returnToPath, setReturnToPath] = useState(null) 

  // Paiement (Suppression de paymentLink state pour éviter les fantômes, on utilise currentApt direct)
  const [loadingPayment, setLoadingPayment] = useState(false)
  const [stancerAmount, setStancerAmount] = useState('') 
  const[activeManualPayment, setActiveManualPayment] = useState(null)
  const [manualPaymentAmount, setManualPaymentAmount] = useState('')

  // Remboursement inline
  const[activeRefundId, setActiveRefundId] = useState(null)
  const [refundAmountInput, setRefundAmountInput] = useState('')
  
  // Filtres Liste
  const [filter, setFilter] = useState(initialFilter)
  const [search, setSearch] = useState('')
  const [historyOffset, setHistoryOffset] = useState(0)
  const [hasMoreHistory, setHasMoreHistory] = useState(true)
  
  const [resendingVideo, setResendingVideo] = useState(false)
  
  // Modales
  const [selectedApt, setSelectedApt] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [currentApt, setCurrentApt] = useState(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // États pour la création/édition (Stylo intelligent)
  const [filteredPatientsSearch, setFilteredPatientsSearch] = useState([]) 
  const [patientSearch, setPatientSearch] = useState('')
  const [selectedPatientId, setSelectedPatientId] = useState(null)

  const [reasons, setReasons] = useState([])
  const [tariffs, setTariffs] = useState([])
  const [tpps, setTpps] = useState([])
  const [sysSettings, setSysSettings] = useState({ price_new: 60, price_followup: 40 })
  const [newAptData, setNewAptData] = useState({
    date: '',
    start_time: '',
    consultation_type: 'cabinet',
    patient_type: 'new',
    reason_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    price: 60, // Valeur par défaut pour éviter les oublis
    silent_update: true, // Par défaut, on active le mode "mise à jour silencieuse" pour éviter les notifications intempestives lors de la création depuis l'admin
    custom_duration: ''
  })

  // UX Améliorée : Suggestions et Conflits
  const [slotSuggestions, setSlotSuggestions] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [conflictWarning, setConflictWarning] = useState(null)
  
  // État pour le SMS rapide
  const [smsMessage, setSmsMessage] = useState('')
  const [sendingSms, setSendingSms] = useState(false)

  const calendarRef = useRef(null)


  // --- INITIALISATION ---

  const resetNewAptData = () => {
    setNewAptData({
      date: '',
      start_time: '',
      consultation_type: 'cabinet',
      patient_type: 'new',
      reason_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      notes: '',
      price: sysSettings.price_new, // PRIX DYNAMIQUE D'APRÈS LES REGLAGES
      silent_update: true // Par défaut, on active le mode "mise à jour silencieuse" pour éviter les notifications intempestives lors de la création depuis l'admin
    })
    setPatientSearch('')
    setSelectedPatientId(null)
    setSlotSuggestions([])
    setConflictWarning(null)
  }

  // --- DATA FETCHING ---

  const fetchAppointmentsList = (append = false, currentOffset = 0) => {
    if (!append) setLoading(true)
    const params = {}
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]

    if (filter === 'today') {
      params.from_date = todayStr
      params.to_date = todayStr
    } else if (filter === 'week') {
      // Du Lundi au Dimanche de la semaine courante
      const startOfWeek = new Date(today);
      const day = startOfWeek.getDay();
      const diffToMonday = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diffToMonday);
      params.from_date = startOfWeek.toISOString().split('T')[0];

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      params.to_date = endOfWeek.toISOString().split('T')[0];
    } else if (filter === 'upcoming') {
      params.from_date = todayStr
      params.status = 'confirmed'
      params.limit = 100 
    } else if (filter === 'cancelled') {
      params.status = 'cancelled'
      params.limit = 50 
    } else if (filter === 'history') {
      params.is_history = true
      params.limit = 15
      params.offset = currentOffset
    }

    api.get('/api/appointments/', { params })
      .then(res => {
          if (append) {
              setAppointments(prev => [...prev, ...res.data])
              setHasMoreHistory(res.data.length === 15)
          } else {
              setAppointments(res.data)
              if (filter === 'history') setHasMoreHistory(res.data.length === 15)
          }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  const handleLoadMoreHistory = () => {
      const newOffset = historyOffset + 15
      setHistoryOffset(newOffset)
      fetchAppointmentsList(true, newOffset)
  }


  const fetchAppointmentsCalendar = (start, end) => {
    Promise.all([
        api.get('/api/appointments/', { params: { from_date: start, to_date: end } }),
        api.get('/api/tasks/?pending_only=true') // On charge aussi les tâches en attente
    ])
      .then(([aptsRes, tasksRes]) => {
        setAppointments(aptsRes.data) // 🛡️ CORRECTION : On met aussi la base à jour pour le détecteur de conflits !
        
        // On exclut les RDV annulés de la vue Calendrier pour libérer visuellement la case cliquable !
        const events = aptsRes.data
          .filter(apt => apt.status !== 'cancelled')
          .map(apt => ({
            id: apt.id.toString(),
            title: apt.patient_name,
            start: `${apt.date}T${apt.start_time}`,
            end: `${apt.date}T${apt.end_time}`,
            backgroundColor: CONSULTATION_TYPES[apt.consultation_type]?.color || '#888',
            borderColor: CONSULTATION_TYPES[apt.consultation_type]?.color || '#888',
            extendedProps: { ...apt }
        }))

        // On transforme les tâches avec une date en événements "allDay" (Post-it jaunes)
        const taskEvents = tasksRes.data
            .filter(t => t.due_date) // Seulement si elle a une date
            .map(t => ({
                id: `task-${t.id}`,
                title: t.patient_name ? `${t.patient_name} : ${t.content}` : t.content,
                start: t.due_date,
                allDay: true, // S'affiche tout en haut
                backgroundColor: '#fef08a', // Jaune
                borderColor: '#fef08a',
                textColor: '#854d0e',
                classNames:['task-event', 'cursor-pointer', 'hover:opacity-80'],
                extendedProps: { type: 'task', data: t }
            }))

        setCalendarEvents([...events, ...taskEvents])
      })
      .catch(console.error)
  }

  const refreshData = () => {
    fetchAppointmentsList()
    if (calendarRef.current) {
        const apiCal = calendarRef.current.getApi()
        fetchAppointmentsCalendar(
            apiCal.view.activeStart.toISOString().split('T')[0], 
            apiCal.view.activeEnd.toISOString().split('T')[0]
        )
    }
  }

  useEffect(() => {
    if (viewMode === 'list') {
      setHistoryOffset(0) // On reset l'offset quand on change d'onglet
      fetchAppointmentsList(false, 0)
    }
  }, [viewMode, filter])

  useEffect(() => {
    if (createModalOpen) {
      // On ne télécharge plus tous les patients d'un coup !
      api.get('/api/admin/reasons').then(res => setReasons(res.data))
      api.get('/api/admin/tariffs').then(res => setTariffs(res.data))

      api.get('/api/admin/third-party-payers').then(res => setTpps(res.data))
      api.get('/api/settings/').then(res => {
              const conf = {};
              res.data.forEach(s => conf[s.setting_key] = s.setting_value);
              
              const pNew = parseFloat(conf.price_new) || 60;
              const pFol = parseFloat(conf.price_followup) || 40;
              const dNew = parseInt(conf.appointment_new_duration) || 60;
              const dFol = parseInt(conf.appointment_followup_duration) || 30;
              
              setSysSettings({ price_new: pNew, price_followup: pFol, duration_new: dNew, duration_followup: dFol });
              
              // On n'écrase le prix par défaut QUE si on crée un nouveau RDV (Sécurité anti-écrasement !)
              setNewAptData(prev => {
                  if (isEditing) return prev; 
                  return {
                      ...prev,
                      price: prev.patient_type === 'new' ? pNew : pFol
                  }
              });
          })
        }
      },[createModalOpen, isEditing])


  // --- LOGIQUE SUGGESTIONS & CONFLITS ---

  useEffect(() => {
    if (createModalOpen && newAptData.date) {
        setLoadingSlots(true)
        setSlotSuggestions([])

        api.get('/api/slots/available', {
            params: {
                from_date: newAptData.date,
                to_date: newAptData.date,
                consultation_type: newAptData.consultation_type,
                patient_type: newAptData.patient_type,
                is_admin: true,
                exclude_appointment_id: newAptData.id || undefined // <-- On passe l'ID si on est en édition !
            }
        }).then(res => {
            setSlotSuggestions(res.data)
        }).catch(err => {
            console.error("Erreur chargement slots", err)
        }).finally(() => {
            setLoadingSlots(false)
        })
    }
  }, [createModalOpen, newAptData.date, newAptData.consultation_type, newAptData.patient_type])

  useEffect(() => {
    if (!createModalOpen || !newAptData.date || !newAptData.start_time) {
        setConflictWarning(null)
        return
    }

    const startParts = newAptData.start_time.split(':').map(Number)
    const startMinutes = startParts[0] * 60 + startParts[1]
    
    // 🛡️ CORRECTION : On utilise la durée personnalisée ou les vrais réglages du cabinet !
    const duration = newAptData.custom_duration 
        ? newAptData.custom_duration 
        : (newAptData.patient_type === 'new' ? (sysSettings.duration_new || 60) : (sysSettings.duration_followup || 30));
        
    const endMinutes = startMinutes + duration

    const collision = appointments.find(apt => {

        if (apt.id === newAptData.id) return false
        if (apt.status === 'cancelled') return false
        if (apt.date !== newAptData.date) return false

        const aptStart = apt.start_time.split(':').map(Number)
        const aptStartMin = aptStart[0] * 60 + aptStart[1]
        const aptEnd = apt.end_time.split(':').map(Number)
        const aptEndMin = aptEnd[0] * 60 + aptEnd[1]

        return (startMinutes < aptEndMin && endMinutes > aptStartMin)
    })

    if (collision) {
                setConflictWarning(`⚠️ Chevauchement avec ${collision.patient_name} (${formatTime(collision.start_time)} - ${formatTime(collision.end_time)})`)
            } else {
                setConflictWarning(null)
            }

        // 🛡️ CORRECTION : On ajoute custom_duration et les paramètres système (sysSettings) pour recalculer en direct
        }, [createModalOpen, newAptData.date, newAptData.start_time, newAptData.id, newAptData.patient_type, newAptData.custom_duration, appointments, sysSettings])
 
  
// Remise à zéro quand on change de RDV cible
  useEffect(() => {
      if (currentApt) {
          setConflictWarning(null)
          setSmsMessage('')
          setActiveManualPayment(null)
          setManualPaymentAmount('')
          
          // --- CALCUL DU RESTE À PAYER POUR L'INPUT ---
          const price = parseFloat(currentApt.price || 0);
          const paid = parseFloat(currentApt.amount_paid || 0);
          const remaining = Math.max(0, price - paid);
          
          // Calcul anticipé de la part Tiers-Payant pour ajuster ce que le patient doit payer
          let tppAmt = 0;
          if (currentApt.third_party_payer) {
              const t = currentApt.third_party_payer.coverage_type;
              const v = parseFloat(currentApt.third_party_payer.coverage_value || 0);
              if (t === 'full') tppAmt = price;
              else if (t === 'percentage') tppAmt = price * (v / 100);
              else if (t === 'fixed') tppAmt = v;
              tppAmt = Math.min(tppAmt, price);
              
              // Si la part Tiers-Payant a DÉJÀ été encaissée, elle est incluse dans "paid", on l'ignore ici
              const tppAlreadyPaid = currentApt.transactions?.some(tr => tr.payment_method === 'third_party' && tr.status === 'paid' && parseFloat(tr.amount) > 0);
              if (tppAlreadyPaid) tppAmt = 0;
          }
          
          // Calcul des liens déjà en attente pour ne pas les reproposer
          const pendingAmount = currentApt.transactions
              ?.filter(t => t.status === 'pending' || t.status === 'forced')
              .reduce((sum, t) => sum + parseFloat(t.amount), 0) || 0;

          // Le reste à charge réel pour le patient
          const patientRemaining = Math.max(0, remaining - tppAmt - pendingAmount);
          
          setStancerAmount(patientRemaining > 0 ? patientRemaining.toFixed(2) : '');
      }
  },[currentApt])


  // --- EFFET D'OUVERTURE AUTOMATIQUE  ---
  // Gestion de la navigation depuis la page Patients
    useEffect(() => {
        // Cas 1 : On arrive avec un patient dans le "cartable" (state)
        if (location.state?.targetPatient) {
            const patient = location.state.targetPatient
            setPendingPatient(patient)
            
            // On prévient l'admin visuellement
            toast(`📅 Sélectionnez un créneau pour ${patient.first_name} ${patient.last_name}`, {
                id: 'patient-booking-toast',
                icon: '👋',
                duration: 6000,
                style: {
                    border: '2px solid #7d9668',
                    padding: '16px',
                    color: '#d1f8ba',
                },
            })
            
            // Nettoyage absolu de React Router pour ne pas re-déclencher au moindre clic
            navigate(location.pathname, { replace: true, state: {} })
        }
        
        // Cas 2 : Ouverture d'un RDV existant via URL
        const action = searchParams.get('action')
        const aptId = searchParams.get('apt_id')
        const returnTo = searchParams.get('return_to') // <-- On lit le paramètre
        
        if (action === 'open' && aptId && appointments.length > 0) {
            const apt = appointments.find(a => a.id === parseInt(aptId))
            if (apt) {
                setCurrentApt(apt)
                setDetailModalOpen(true)
                if (returnTo) setReturnToPath(returnTo) // <-- On le sauvegarde en mémoire vive
                setSearchParams({}, { replace: true }) // <-- On nettoie l'URL proprement
            }
        }
    }, [location, searchParams, appointments])

  // --- HELPERS & HANDLERS ---

  const handleResendVideo = async () => {
    setResendingVideo(true)
    try {
        await api.post(`/api/appointments/${currentApt.id}/resend-video`)
        toast.success("Lien visio renvoyé au patient !")
    } catch (error) {
        toast.error(error.response?.data?.detail || "Erreur lors de l'envoi")
    } finally {
        setResendingVideo(false)
    }
  }

  const closeDetailModal = () => {
      // On utilise notre mémoire au lieu de l'URL !
      if (returnToPath) {
          navigate(returnToPath);
      } else {
          setDetailModalOpen(false);
          setSearchParams({}, { replace: true });
      }
  };


  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/api/appointments/${id}/status?status=${newStatus}`)
      toast.success('Statut mis à jour')
      refreshData()
      setSelectedApt(null)
      setDetailModalOpen(false)
    } catch (error) {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const handleSendSms = async () => {
    if (!smsMessage.trim()) return
    setSendingSms(true)
    try {
        await api.post('/api/sms/test', {
            phone: currentApt.patient_phone,
            message: smsMessage
        })
        toast.success('SMS envoyé au patient')
        setSmsMessage('')
    } catch (error) {
        toast.error(error.response?.data?.detail || "Erreur lors de l'envoi")
    } finally {
        setSendingSms(false)
    }
  }

  const handleSelect = (selectInfo) => {
    // ... (code existant de gestion vue mois/semaine) ...
    if (selectInfo.view.type === 'dayGridMonth') {
        const calendarApi = calendarRef.current.getApi()
        calendarApi.changeView('timeGridWeek', selectInfo.startStr)
        return
    }

    resetNewAptData()
    setIsEditing(false)
    
    // Préparation des dates
    const startStr = selectInfo.startStr.split('T')[1].slice(0, 5)
    
    // --- LA MAGIE EST ICI ---
    if (pendingPatient) {
        // Si on a un patient en attente, on pré-remplit !
        selectExistingPatient(pendingPatient)
        
        // On vide la mémoire tampon pour le prochain tour
        setPendingPatient(null) 
    }
    // -----------------------

    setNewAptData(prev => ({
      ...prev,
      date: selectInfo.startStr.split('T')[0],
      start_time: startStr,
      // On peut garder les autres champs du prev ou du selectExistingPatient
    }))
    setCreateModalOpen(true)
}

  const handleEditClick = (target) => {
    if (!target) {
        toast.error("Données introuvables");
        return;
    }

    const nameParts = target.patient_name ? target.patient_name.split(' ') : ['', ''];
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
        
    let duration = '';
    if (target.start_time && target.end_time) {
        const [sh, sm] = target.start_time.split(':').map(Number);
        const [eh, em] = target.end_time.split(':').map(Number);
        duration = (eh * 60 + em) - (sh * 60 + sm);
        if (duration < 0) duration += 24 * 60;
    }

    const editData = {
      id: target.id,
      date: target.date,
      start_time: target.start_time ? target.start_time.slice(0, 5) : '',
      consultation_type: target.consultation_type || 'cabinet',
      patient_type: target.patient_type || 'new',
      reason_id: target.reason_id || '', 
      first_name: firstName,
      last_name: lastName,
      email: target.patient_email || '',
      phone: target.patient_phone || '',
      notes: target.notes || '',
      price: target.price !== undefined && target.price !== null ? target.price : '',
      tariff_id: target.tariff_id || '',
      third_party_payer_id: target.third_party_payer_id || '',
        silent_update: true, 
        custom_duration: duration
    };

    setNewAptData(editData);
    setIsEditing(true);
    setDetailModalOpen(false);
    setSelectedApt(null);
    setCreateModalOpen(true);
  };

  const handleAdminSubmitApt = async () => {
    if (!newAptData.first_name?.trim() || !newAptData.last_name?.trim()) {
        return toast.error("Le prénom et le nom du patient sont obligatoires.");
    }
    
    try {
      setLoading(true)
      const { id, ...payload } = newAptData
      
      // Nettoyage strict pour le backend (Evite 422)
      if (payload.email === '') payload.email = null
      if (payload.reason_id === '') payload.reason_id = null
      if (payload.price === '') payload.price = null
      if (payload.tariff_id === '') payload.tariff_id = null
      if (payload.third_party_payer_id === '') payload.third_party_payer_id = null
      if (payload.custom_duration === '') payload.custom_duration = null

      if (isEditing) {


        await api.patch(`/api/appointments/${id}`, payload)
        toast.success('Rendez-vous modifié')
      } else {
        await api.post('/api/appointments/', payload)
        toast.success('Rendez-vous créé')
      }
      setCreateModalOpen(false)
      resetNewAptData() 
      refreshData()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setLoading(false)
    }
  }

  const selectExistingPatient = (p) => {
    setSelectedPatientId(p.id)
    setPatientSearch(`${p.first_name} ${p.last_name}`)
    
    // Si le patient a déjà un historique, c'est un suivi !
    const isFollowUp = p.appointment_count > 0;
        setNewAptData(prev => ({
          ...prev,
          first_name: p.first_name,
          last_name: p.last_name,
          email: p.email,
          phone: p.phone,
          patient_type: isFollowUp ? 'follow_up' : 'new',
          price: isFollowUp ? sysSettings.price_followup : sysSettings.price_new,
          custom_duration: isFollowUp ? sysSettings.duration_followup : sysSettings.duration_new,
          tariff_id: '' 
        }))
  }


  // --- GESTION PAIEMENT ---
  const handleSendPaymentLink = async (viaEmail, viaSms) => {
    setLoadingPayment(true);
    try {
        await api.post(`/api/payments/send-link/${currentApt.id}`, {
            via_email: viaEmail,
            via_sms: viaSms
        });
        toast.success(`Lien envoyé par ${viaEmail ? 'Email' : 'SMS'} !`);
    } catch (error) {
        toast.error("Erreur lors de l'envoi du lien");
    } finally {
        setLoadingPayment(false);
    }
  }

  const handleGeneratePaymentLink = async () => {
    if (!currentApt) return
    setLoadingPayment(true)
    try {
        // Envoi du montant spécifique
        const payload = stancerAmount ? { amount: parseFloat(stancerAmount) } : {}
        const res = await api.post(`/api/payments/create-link/${currentApt.id}`, payload)
        
        toast.success("Lien de paiement généré !")
        
        // MAJ LOCALE : On met à jour currentApt directement avec les données reçues
        setCurrentApt(prev => {
            const updatedTransactions = [...(prev.transactions || [])];
            if (res.data.transaction) {
                updatedTransactions.push(res.data.transaction);
            }
            return {
                ...prev, 
                payment_status: 'pending', 
                payment_link: res.data.payment_url,
                payment_id: res.data.payment_id,
                transactions: updatedTransactions
            };
        });

        
        refreshData()

    } catch (error) {
        console.error(error)
        toast.error(error.response?.data?.detail || "Erreur création lien")
    } finally {
        setLoadingPayment(false)
    }
  }

  const handleCheckStatus = async () => {
    
    if (!currentApt?.payment_id) return
    setLoadingPayment(true)
    try {
        const res = await api.post(`/api/payments/check-status/${currentApt.id}`)
        
        if (res.data.status === 'updated') {
            toast.success("Paiement validé et encaissé !");
            
            // MISE A JOUR IMMEDIATE DE LA MODALE (Statut Global ET Statut Transaction)
            setCurrentApt(prev => {
                let safeTransactions = new Array(); // Contournement du bug parseur
                
                if (prev.transactions) {
                    safeTransactions = prev.transactions.map(t => {
                        if (t.status === 'pending' || t.status === 'forced') {
                            return { ...t, status: 'paid' };
                        }
                        return t;
                    });
                }
                
                return { 
                    ...prev, 
                    payment_status: res.data.payment_status,
                    transactions: safeTransactions 
                };
            });
            refreshData(); 
        } else {
            toast('Statut actuel : En attente du patient', { icon: 'ℹ️' });
        }
    } catch (error) {
        console.error(error)
        toast.error("Impossible de vérifier le statut")
    } finally {
        setLoadingPayment(false)
    }
  }


  // Fonction pour valider un paiement manuel
  const handleManualPayment = async (method, amount) => {
    const labels = {
        cash: 'Espèces',
        check: 'Chèque',
        card_terminal: 'TPE',
        transfer: 'Virement',
        discount: 'Remise',
        third_party: 'Tiers-Payant'
    };
    const label = labels[method] || method;
    
    // Validation basique
    if (!amount || isNaN(amount) || amount <= 0) {
        return toast.error("Montant invalide");
    }

    const price = parseFloat(currentApt.price || 0);
    const paid = parseFloat(currentApt.amount_paid || 0);
    const remaining = price - paid;

    // On bloque si ça dépasse (avec une petite marge pour les arrondis JS)
    if (amount > remaining + 0.01) {
        return toast.error("Le montant saisi dépasse le solde restant !");
    }

    setLoadingPayment(true);
    try {
        const res = await api.post(`/api/payments/manual/${currentApt.id}`, {
            method: method,
            amount: parseFloat(amount)
        });
        
        toast.success(`Paiement de ${amount}€ en ${label} enregistré`);
        setActiveManualPayment(null);
        
        // MAJ locale (On ajoute l'argent ET la nouvelle ligne d'historique !)
        setCurrentApt(prev => ({
            ...prev,
            amount_paid: res.data.new_amount_paid,
            payment_status: res.data.payment_status,
            transactions: [...(prev.transactions || []), res.data.transaction]
        }))
        
        refreshData(); // Met à jour la liste en arrière-plan

    } catch (error) {
        toast.error("Erreur enregistrement");
    } finally {
        setLoadingPayment(false);
    }
  }

  const handleDeleteTransaction = async (transId, amount, method) => {
    let confirmMessage = "Annuler cette ligne ? Le solde du RDV sera mis à jour.";
    
    // Avertissement spécifique pour Stancer
    if (method === 'stancer') {
        confirmMessage = "ATTENTION : Le lien Stancer ne peut pas être désactivé à distance. S'il a déjà été envoyé au patient, veuillez lui demander de ne pas l'utiliser, sinon l'encaissement ne sera pas synchronisé avec BellaBlue.\n\nConfirmez-vous la suppression ?";
    } else if (method === 'stripe') {
        confirmMessage = "Désactiver et annuler ce lien Stripe ? Le patient ne pourra plus payer dessus.";
    }

    if (!confirm(confirmMessage)) return;
    
    setLoadingPayment(true);
    try {
        const res = await api.delete(`/api/payments/transaction/${transId}`);

        toast.success("Opération annulée");
        
        // MAJ locale immédiate (On fait 100% confiance aux mathématiques du Backend)
        setCurrentApt(prev => {
            const deletedTrans = prev.transactions?.find(t => t.id === transId);
            const isDeletingActiveLink = deletedTrans && deletedTrans.payment_id === prev.payment_id;

            return {
                ...prev,
                amount_paid: res.data.new_amount_paid, // <--- LA MAGIE EST ICI
                payment_status: res.data.payment_status,
                payment_id: isDeletingActiveLink ? null : prev.payment_id,
                payment_link: isDeletingActiveLink ? null : prev.payment_link,
                transactions: prev.transactions.filter(t => t.id !== transId)
            };
        });
        
        refreshData(); 
    } catch (error) {


        toast.error("Erreur lors de l'annulation");
    } finally {
        setLoadingPayment(false);
    }
  }

const handleForceTransaction = async (transId) => {
    if (!confirm("Forcer la validation de ce paiement ? La facture sera débloquée, mais VERIFIEZ AVANT si le patient a réellement payé sur son lien.")) return;
    setLoadingPayment(true);
    try {
        const res = await api.post(`/api/payments/transaction/${transId}/force`);
        toast.success("Paiement forcé (Facture débloquée)");
        
        // MAJ locale avec les données du backend pour éviter tout crash
        setCurrentApt(prev => ({
            ...prev,
            amount_paid: res.data.new_amount_paid,
            payment_status: res.data.payment_status,
            transactions: prev.transactions ? prev.transactions.map(t => t.id === transId ? { ...t, status: 'forced' } : t) :[]
        }));
        refreshData();
    } catch (e) {
        console.error(e);
        toast.error("Erreur lors du forçage");
    } finally {
        setLoadingPayment(false);
    }
  }



const handleRefundTransaction = async (transId, maxAmount, method) => {
    const refundAmount = parseFloat(refundAmountInput.replace(',', '.'));
    if (isNaN(refundAmount) || refundAmount <= 0 || refundAmount > maxAmount) {
        return toast.error("Montant invalide.");
    }

    const isStancer = method === 'stancer';
    const msg = isStancer 
        ? `Confirmer le remboursement de ${refundAmount}€ via la CB du patient ?\nL'action est irréversible.` 
        : `Enregistrer un remboursement ou avoir de ${refundAmount}€ ?`;
        
    if (!confirm(msg)) return;
    
    setLoadingPayment(true);
    try {
        const res = await api.post(`/api/payments/transaction/${transId}/refund`, {
            amount: refundAmount
        });
        toast.success(isStancer ? "Remboursement Stancer effectué" : "Remboursement enregistré");
        
        // MAJ locale immédiate (On ajoute la nouvelle ligne négative retournée par l'API)
        setCurrentApt(prev => ({
            ...prev,
            amount_paid: res.data.new_amount_paid,
            payment_status: res.data.payment_status,
            transactions: [...(prev.transactions || []), res.data.transaction]
        }));
        
        setActiveRefundId(null);
        setRefundAmountInput('');
        refreshData();
    } catch (error) {
        toast.error(error.response?.data?.detail || "Erreur lors du remboursement");
    } finally {
        setLoadingPayment(false);
    }
  }



  const handlePreviewInvoice = async () => {
    if (!currentApt) return
    try {
        toast.loading("Génération de l'aperçu...", { id: 'invoice' })
        const newWindow = window.open('', '_blank');
        
        const response = await api.post(`/api/invoices/${currentApt.id}/generate`, {}, {
            responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        
        // ASTUCE : On écrit du HTML dans le nouvel onglet pour forcer le titre, et on iframe le PDF !
        newWindow.document.write(`
            <html>
                <head>
                    <title>Facture - ${currentApt.patient_name}</title>
                    <style>body { margin: 0; padding: 0; overflow: hidden; }</style>
                </head>
                <body>
                    <iframe src="${url}" width="100%" height="100%" frameborder="0"></iframe>
                </body>
            </html>
        `);
        newWindow.document.close();
        
        toast.success("Aperçu ouvert !", { id: 'invoice' })
        refreshData() 
    } catch (error) {
        toast.error("Erreur lors de la génération", { id: 'invoice' })
    }
  }


  
  const handleDownloadInvoice = async () => {
    if (!currentApt) return
    try {
        toast.loading("Génération de la facture...")
        
        // On appelle l'endpoint de génération (qui renvoie le binaire)
        const response = await api.post(`/api/invoices/${currentApt.id}/generate`, {}, {
            responseType: 'blob' // Important pour dire à axios que c'est un fichier
        })
        
        // Création d'un lien invisible pour télécharger le fichier
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Nom du fichier (on essaie de faire propre)
        const fileName = `Facture_${currentApt.patient_name.replace(/\s+/g, '_')}_${currentApt.date}.pdf`;
        link.setAttribute('download', fileName);
        
        document.body.appendChild(link);
        link.click();
        
        // Nettoyage
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.dismiss()
        toast.success("Facture téléchargée !")
        
        // On met à jour pour afficher que la facture existe (si on a ajouté le champ dans le modèle front)
        refreshData() 
    } catch (error) {
        toast.dismiss()
        console.error(error)
        toast.error("Erreur lors du téléchargement")
    }
  }


  // --- RENDERING UTILS ---

  const formatTime = (time) => time?.slice(0, 5) || ''
  
  const handleDatesSet = (dateInfo) => {
    if (viewMode === 'calendar') {
      fetchAppointmentsCalendar(
        dateInfo.start.toISOString().split('T')[0],
        dateInfo.end.toISOString().split('T')[0]
      )
    }
  }

  const handleDateClick = (info) => {
    if (info.view.type === 'dayGridMonth') {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.changeView('timeGridWeek', info.dateStr)
    }
  }

  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps.type === 'task') {
        const task = clickInfo.event.extendedProps.data;
        if (confirm(`Terminer cette tâche ?\n\n"${task.content}"`)) {
            api.patch(`/api/tasks/${task.id}`, { is_completed: true })
                .then(() => {
                    toast.success("Tâche terminée !");
                    refreshData();
                })
        }
        return; // On arrête là pour ne pas ouvrir la modale de RDV
    }
    setCurrentApt(clickInfo.event.extendedProps)
    setSmsMessage('')
    setDetailModalOpen(true)
  }

  const renderEventContent = (eventInfo) => {
    const apt = eventInfo.event.extendedProps
    const type = apt.consultation_type
    const Icon = CONSULTATION_TYPES[type]?.icon || User

    if (eventInfo.view.type === 'dayGridMonth') {
      return (
        <div 
            className="flex items-center gap-1 w-full h-full px-1 py-0.5 rounded-sm overflow-hidden"
            style={{ backgroundColor: CONSULTATION_TYPES[type]?.bg }}
        >
             <span className="text-[10px] truncate font-medium" style={{ color: CONSULTATION_TYPES[type]?.color }}>
                {apt.status === 'cancelled' ? '❌ ' : ''}
                {eventInfo.timeText.replace(':', 'h')} {eventInfo.event.title}
             </span>
        </div>
      )
    }

    return (
      <div className="flex flex-col px-1 overflow-hidden h-full leading-tight">
        <div className="flex items-center gap-1 font-bold text-xs">
            <Icon className="w-3 h-3" />
            <span className="truncate">{eventInfo.timeText}</span>
        </div>
        <span className="text-xs truncate">{eventInfo.event.title}</span>
        {apt.status === 'cancelled' && <span className="text-[10px] bg-red-500 text-white px-1 rounded w-fit">ANNULÉ</span>}
      </div>
    )
  }
  
  const filteredAppointments = appointments.filter(apt => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      apt.patient_name?.toLowerCase().includes(searchLower) ||
      apt.patient_email?.toLowerCase().includes(searchLower) ||
      apt.patient_phone?.includes(search)
    )
  })

  // --- RECHERCHE PATIENT DYNAMIQUE ---
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (patientSearch.length > 1 && !selectedPatientId) {
        // On interroge le backend qui va fouiller TOUTE la base (Nom, Téléphone, etc.)
        api.get('/api/patients/', { params: { search: patientSearch, limit: 5 } })
           .then(res => setFilteredPatientsSearch(res.data))
           .catch(console.error)
      } else {
        setFilteredPatientsSearch([])
      }
    }, 300) // On attend 300ms de "silence clavier" pour ne pas saturer le serveur
    return () => clearTimeout(delayDebounceFn)
  }, [patientSearch, selectedPatientId])

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col">
      {/* HEADER */}
      <div className="mb-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif text-gray-800">Rendez-vous</h1>
          <p className="text-gray-500 mt-1">Gérez le planning des consultations.</p>
        </div>
        {/* Recherche */}
        <div className="flex gap-4">
            {viewMode === 'list' && (
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm w-48 focus:w-64 transition-all"
                    />
                </div>
            )}

            {/* Mode List/Calendar */}
            <div className="flex bg-gray-100 p-1 rounded-xl">
                <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}
                    title="Vue Liste"
                >
                    <ListIcon className="w-5 h-5" />
                </button>
                <button
                    onClick={() => setViewMode('calendar')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}
                    title="Vue Calendrier"
                >
                    <CalendarIcon className="w-5 h-5" />
                </button>
            </div>
            
        </div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div className="flex-1 overflow-hidden">
        
        {/* --- VUE LISTE --- */}
        {viewMode === 'list' && (
            <div className="h-full flex flex-col">
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
                    {[
                        { value: 'today', label: "Aujourd'hui" },
                        { value: 'week', label: 'Cette semaine' },
                        { value: 'upcoming', label: 'À venir (Max 100)' },
                        { value: 'history', label: 'Historique' },
                        { value: 'cancelled', label: 'Annulés' },
                    ].map(tab => (
                        <button
                        key={tab.value}
                        onClick={() => setFilter(tab.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            filter === tab.value
                            ? 'bg-sage-600 text-white shadow-md'
                            : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                        }`}
                        >
                        {tab.label}
                        </button>
                    ))}
                    {/* Mode List/Calendar bis*/}
                    <div className="flex bg-gray-100 p-1 rounded-xl">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}
                            title="Vue Liste"
                        >
                            <ListIcon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('calendar')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}
                            title="Vue Calendrier"
                        >
                            <CalendarIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                    {loading ? (
                        <div className="flex justify-center py-12"><div className="spinner" /></div>
                    ) : filteredAppointments.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                            <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500">Aucun rendez-vous trouvé</p>
                        </div>
                    ) : (
                        filteredAppointments.map(apt => {
                            const status = STATUS_CONFIG[apt.status] || STATUS_CONFIG.confirmed
                            const type = CONSULTATION_TYPES[apt.consultation_type] || CONSULTATION_TYPES.cabinet
                            
                            return (
                            <div key={apt.id} className="card hover:shadow-md transition-shadow group cursor-pointer" onClick={() => { setCurrentApt(apt); setDetailModalOpen(true); }}>
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                <div className="flex items-center gap-4 lg:w-48">
                                    <div className="w-14 h-14 bg-sage-50 rounded-xl flex flex-col items-center justify-center border border-sage-100">
                                    <span className="text-xs text-sage-600 uppercase font-bold">
                                        {new Date(apt.date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                                    </span>
                                    <span className="text-xl font-bold text-sage-800">
                                        {new Date(apt.date).getDate()}
                                    </span>
                                    <span className="text-xs uppercase font-bold">{new Date(apt.date).toLocaleDateString('fr-FR', { month: 'short' })}</span>
                                    </div>
                                    <div>
                                    <p className="font-medium text-gray-800">
                                        {formatTime(apt.start_time)} - {formatTime(apt.end_time)}
                                    </p>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                                        {type.label}
                                    </div>
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/patients/${apt.patient_id}`); }}
                                            className="font-bold text-gray-800 text-lg hover:text-sage-600 hover:underline transition-colors text-left"
                                        >
                                            {apt.patient_name}
                                        </button>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${apt.patient_type === 'new' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {apt.patient_type === 'new' ? 'Nouveau' : 'Suivi'}
                                        </span>
                                    </div>

                                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500 mt-1">
                                    <span className="flex items-center gap-1.5 hover:text-sage-600 transition-colors">
                                        <Mail className="w-3.5 h-3.5" />
                                        {apt.patient_email}
                                    </span>
                                    <span className="flex items-center gap-1.5 hover:text-sage-600 transition-colors">
                                        <Phone className="w-3.5 h-3.5" />
                                        {apt.patient_phone}
                                    </span>
                                    </div>
                                    {apt.reason_name && (
                                    <p className="text-sm text-sage-600 mt-2 bg-sage-50 w-fit px-2 py-1 rounded">
                                        📋 {apt.reason_name}
                                    </p>
                                    )}
                                </div>

                                <div className="flex items-center gap-3">
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                    <status.icon className="w-3.5 h-3.5" />
                                    {status.label}
                                    </span>
                                    
                                    <div className="relative">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedApt(selectedApt === apt.id ? null : apt.id); }}
                                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <MoreVertical className="w-5 h-5" />
                                    </button>
                                    
                                    {selectedApt === apt.id && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-20 animate-in fade-in zoom-in duration-200">
                                        <button onClick={(e) => { e.stopPropagation(); handleEditClick(apt); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                                            <Save className="w-4 h-4 text-sage-600" /> Modifier
                                        </button>
                                        {apt.status !== 'completed' && (
                                            <button onClick={(e) => { e.stopPropagation(); handleStatusChange(apt.id, 'completed'); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                                            <CheckCircle className="w-4 h-4 text-green-500" /> Marquer terminé
                                            </button>
                                        )}
                                        {apt.status !== 'cancelled' && (
                                            <button onClick={(e) => { e.stopPropagation(); handleStatusChange(apt.id, 'cancelled'); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600">
                                            <XCircle className="w-4 h-4" /> Annuler
                                            </button>
                                        )}
                                        </div>
                                    )}
                                    </div>
                                </div>
                                </div>
                            </div>
                            )
                        })
                    )}
                    
                    {filter === 'history' && hasMoreHistory && !loading && appointments.length > 0 && (
                        <div className="flex justify-center mt-6 mb-8">
                            <button onClick={handleLoadMoreHistory} className="btn bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shadow-sm px-6">
                                Charger plus de rendez-vous...
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}

        {/* --- VUE CALENDRIER --- */}
        {viewMode === 'calendar' && (
            <div className="flex flex-col h-full space-y-4">
                {/* Mode List/Calendar */}
                <div className="flex justify-start flex-shrink-0">
                    <div className="flex bg-gray-100 p-1 rounded-xl">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}
                            title="Vue Liste"
                        >
                            <ListIcon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('calendar')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}
                            title="Vue Calendrier"
                        >
                            <CalendarIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Calendrier */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex-1 min-h-0 ">
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                        initialView="timeGridWeek"
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek'
                        }}
                        locale={frLocale}
                        firstDay={1}
                        allDaySlot={true}
                        allDayText="Tâches"
                        slotMinTime="07:00:00"
                        slotMaxTime="21:00:00"
                        height="100%"
                        slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                        selectable={true}
                        selectMirror={true}
                        select={handleSelect}
                        events={calendarEvents}
                        eventContent={renderEventContent}
                        datesSet={handleDatesSet}
                        dateClick={handleDateClick}
                        eventClick={handleEventClick}
                        selectLongPressDelay={500} // <-- La magie mobile
                    />
                </div>
            </div>
        )}
      </div>

      {/* MODALE DÉTAIL RDV */}
      {detailModalOpen && currentApt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl transform transition-all scale-100 overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className={`px-6 py-4 flex justify-between items-start ${CONSULTATION_TYPES[currentApt.consultation_type]?.bg}`}>
                    <div>
                        <h3 className="text-xl font-bold text-gray-800">{currentApt.patient_name}</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                            {new Date(currentApt.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            <span>•</span>
                            {formatTime(currentApt.start_time)} - {formatTime(currentApt.end_time)}
                        </p>
                    </div>
                    <button onClick={closeDetailModal} className="p-1 hover:bg-white/50 rounded-full transition-colors">
                        <X className="w-6 h-6 text-gray-600" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Statut & Type */}
                    <div className="flex gap-4">
                        <div className={`flex-1 p-3 rounded-xl border flex items-center gap-3 ${STATUS_CONFIG[currentApt.status]?.color} bg-opacity-10 border-opacity-20`}>
                            {(() => {
                                const StatusIcon = STATUS_CONFIG[currentApt.status]?.icon || AlertCircle
                                return <StatusIcon className="w-5 h-5" />
                            })()}
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Statut</p>
                                <p className="font-medium">{STATUS_CONFIG[currentApt.status]?.label}</p>
                            </div>
                        </div>
                        <div className="flex-1 p-3 rounded-xl border border-gray-100 bg-gray-50 flex items-center gap-3">
                            {(() => {
                                const TypeIcon = CONSULTATION_TYPES[currentApt.consultation_type]?.icon || Building
                                return <TypeIcon className="w-5 h-5 text-gray-500" />
                            })()}
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Lieu</p>
                                <p className="font-medium text-gray-700">{CONSULTATION_TYPES[currentApt.consultation_type]?.label}</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* SECTION PAIEMENT & FACTURATION */}
                    {(() => {
                        // 1. Calculs locaux pour l'affichage
                        const price = parseFloat(currentApt.price || 0);
                        let paid = parseFloat(currentApt.amount_paid || 0);
                        
                        // RÉTRO-COMPATIBILITÉ : Si l'ancien système a marqué "paid" mais laissé amount_paid à 0
                        if (currentApt.payment_status === 'paid' && paid === 0 && price > 0) {
                            paid = price;
                        }
                        
                        const remaining = Math.max(0, price - paid);
                        
                        // Est considéré comme "Tout payé" si le statut global est "paid" ou si le reste est < 0.01
                        const isFullyPaid = currentApt.payment_status === 'paid' || (remaining < 0.01 && price > 0);
                        
                        // Pourcentage pour la barre de progression
                        const percentPaid = isFullyPaid ? 100 : (price > 0 ? Math.min((paid / price) * 100, 100) : 0);

                        // --- CALCUL TIERS PAYANT ---
                        let tppAmount = 0;
                        let tppName = '';
                        let hasTpp = false;
                        let tppAlreadyPaid = false;

                        if (currentApt.third_party_payer) {
                            hasTpp = true;
                            tppName = currentApt.third_party_payer.name;
                            const t = currentApt.third_party_payer.coverage_type;
                            const v = parseFloat(currentApt.third_party_payer.coverage_value || 0);
                            
                            if (t === 'full') tppAmount = price;
                            else if (t === 'percentage') tppAmount = price * (v / 100);
                            else if (t === 'fixed') tppAmount = v;
                            tppAmount = Math.min(tppAmount, price);

                            // On vérifie si l'encaissement a déjà été fait
                            tppAlreadyPaid = currentApt.transactions?.some(tr => tr.payment_method === 'third_party' && tr.status === 'paid' && parseFloat(tr.amount) > 0);
                        }

                        // Calcul dynamique du reste à proposer (Total - Payé - Tiers Payant - Liens en attente)
                        const pendingAmount = currentApt.transactions?.filter(t => t.status === 'pending' || t.status === 'forced').reduce((sum, t) => sum + parseFloat(t.amount), 0) || 0;
                        const manualProposedAmount = Math.max(0, remaining - tppAmount - pendingAmount);

                        return (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
                                
                                {/* A. EN-TÊTE DU BLOC */}
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-xs font-bold text-blue-800 uppercase flex items-center gap-2">
                                        <CreditCard className="w-4 h-4"/> Règlement
                                    </h4>
                                    
                                    <div className="flex items-center gap-2">
                                        {/* Bouton Refresh (si un paiement est en cours de validation) */}
                                        {currentApt.payment_status === 'pending' && (
                                            <button 
                                                onClick={handleCheckStatus}
                                                disabled={loadingPayment}
                                                className="p-1 rounded-full hover:bg-blue-200 text-blue-600 transition-colors"
                                                title="Vérifier le statut chez Stancer"
                                            >
                                                <RefreshCw className={`w-3.5 h-3.5 ${loadingPayment ? 'animate-spin' : ''}`} />
                                            </button>
                                        )}

                                        {/* Badge Statut Global */}
                                        <span className={`text-xs px-2 py-1 rounded-full font-bold uppercase border ${
                                            isFullyPaid 
                                                ? 'bg-green-100 text-green-700 border-green-200' 
                                                : paid > 0 
                                                    ? 'bg-blue-100 text-blue-700 border-blue-200' // Statut Partiel
                                                    : 'bg-gray-100 text-gray-600 border-gray-200'
                                        }`}>
                                            {isFullyPaid ? 'PAYÉ ✅' : paid > 0 ? `PARTIEL (${paid}€)` : 'NON PAYÉ'}
                                        </span>
                                    </div>
                                </div>

                                {/* B. BARRE DE PROGRESSION (Si un prix est défini) */}
                                {price > 0 && (
                                    <div className="mb-4">
                                        <div className="flex justify-between text-[10px] text-blue-800 mb-1 font-medium">
                                            <span>Déjà réglé : {paid.toFixed(2)} €</span>
                                            <span>Total : {price.toFixed(2)} €</span>
                                        </div>
                                        <div className="w-full bg-blue-200/50 rounded-full h-2 overflow-hidden">
                                            <div 
                                                className={`h-full transition-all duration-500 ${isFullyPaid ? 'bg-green-500' : 'bg-blue-500'}`}
                                                style={{ width: `${percentPaid}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* 2.5 TIERS PAYANT (Si applicable et non encaissé) */}
                                {hasTpp && !tppAlreadyPaid && !isFullyPaid && (
                                    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex justify-between items-center shadow-sm">
                                        <div>
                                            <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Prise en charge Tiers-Payant</p>
                                            <p className="text-sm font-bold text-indigo-900">{tppName} <span className="font-black ml-1">({tppAmount.toFixed(2)} €)</span></p>
                                        </div>
                                        <button 
                                            onClick={() => handleManualPayment('third_party', tppAmount)}
                                            disabled={loadingPayment}
                                            className="btn bg-indigo-600 hover:bg-indigo-700 text-white text-xs py-1.5 px-4 shadow-md"
                                        >
                                            Facturer
                                        </button>
                                    </div>
                                )}

                                {/* C. ACTIONS DE PAIEMENT (Si reste à payer) */}
                                {!isFullyPaid && (
                                    <div className="space-y-3 p-3 bg-white/60 rounded-lg border border-blue-100">
                                        {/* 1. Génération de lien Stancer */}
                                        <div className="flex items-end gap-2">
                                            <div className="flex-1">
                                                <label className="text-[10px] font-bold text-blue-800 uppercase block mb-1">
                                                    {paid > 0 ? "Montant du solde à régler" : "Montant à régler en ligne"}
                                                </label>
                                                <div className="relative">
                                                    <input 
                                                        type="number" 
                                                        value={stancerAmount}
                                                        onChange={(e) => setStancerAmount(e.target.value)}
                                                        className="w-full text-sm border-blue-200 rounded-lg pl-3 pr-6 py-2 text-right font-bold text-blue-900 focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-all"
                                                        placeholder={remaining.toFixed(2)}
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-blue-800">€</span>
                                                </div>
                                            </div>

                                            <button 
                                                onClick={handleGeneratePaymentLink}
                                                disabled={loadingPayment}
                                                className="btn bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 py-2 px-4 h-[38px] text-xs font-bold shadow-sm"
                                            >
                                                {loadingPayment ? <div className="spinner w-4 h-4 text-white"/> : (paid > 0 ? "Lien CB Solde" : "Lien CB")}
                                            </button>
                                        </div>

                                        {/* 2. Paiements Manuels & Remise */}
                                        <div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 text-center">Encaissement manuel ou geste commercial</p>
                                            
                                            {!activeManualPayment ? (
                                                <div className="grid grid-cols-5 gap-1.5">
                                                    <button 
                                                        onClick={() => { setActiveManualPayment('card_terminal'); setManualPaymentAmount(manualProposedAmount.toFixed(2)); }}
                                                        className="btn bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-[10px] py-1.5 px-1 font-bold shadow-sm"
                                                        title="Terminal CB Physique"
                                                    >
                                                        💳 TPE
                                                    </button>
                                                    <button 
                                                        onClick={() => { setActiveManualPayment('check'); setManualPaymentAmount(manualProposedAmount.toFixed(2)); }}
                                                        className="btn bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-[10px] py-1.5 px-1"
                                                    >
                                                        🖋️ Chq.
                                                    </button>
                                                    <button 
                                                        onClick={() => { setActiveManualPayment('cash'); setManualPaymentAmount(manualProposedAmount.toFixed(2)); }}
                                                        className="btn bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-[10px] py-1.5 px-1"
                                                    >
                                                        💶 Esp.
                                                    </button>
                                                    <button 
                                                        onClick={() => { setActiveManualPayment('transfer'); setManualPaymentAmount(manualProposedAmount.toFixed(2)); }}
                                                        className="btn bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-[10px] py-1.5 px-1"
                                                        title="Virement Bancaire"
                                                    >
                                                        🏦 Vir.
                                                    </button>
                                                    <button 
                                                        onClick={() => { setActiveManualPayment('discount'); setManualPaymentAmount(manualProposedAmount.toFixed(2)); }}
                                                        className="btn bg-white border border-purple-200 text-purple-700 hover:bg-purple-50 text-[10px] py-1.5 px-1"
                                                    >
                                                        🎁 Rem.
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className={`${activeManualPayment === 'discount' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'} p-2 rounded-lg border animate-in fade-in zoom-in-95`}>
                                                    <label className={`text-[10px] font-bold uppercase mb-1 block ${activeManualPayment === 'discount' ? 'text-purple-800' : 'text-blue-800'}`}>
                                                        Montant : {activeManualPayment === 'cash' ? 'Espèces' : activeManualPayment === 'check' ? 'Chèque' : activeManualPayment === 'card_terminal' ? 'TPE' : activeManualPayment === 'transfer' ? 'Virement' : 'Remise / Offert'}
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <div className="relative flex-1">
                                                            <input 
                                                                type="number" 
                                                                value={manualPaymentAmount}
                                                                onChange={e => setManualPaymentAmount(e.target.value)}
                                                                max={remaining}
                                                                className={`w-full text-xs py-1.5 pr-6 rounded focus:ring-opacity-50 font-bold ${activeManualPayment === 'discount' ? 'border-purple-200 text-purple-900 focus:ring-purple-300' : 'border-blue-200 text-blue-900 focus:ring-blue-300'}`}
                                                            />
                                                            <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${activeManualPayment === 'discount' ? 'text-purple-800' : 'text-blue-800'}`}>€</span>
                                                        </div>
                                                        <button 
                                                            onClick={() => handleManualPayment(activeManualPayment, parseFloat(manualPaymentAmount))}
                                                            disabled={loadingPayment}
                                                            className={`btn px-3 py-1.5 text-xs shadow-sm text-white ${activeManualPayment === 'discount' ? 'bg-purple-600 hover:bg-purple-700' : 'btn-primary'}`}
                                                        >
                                                            Valider
                                                        </button>
                                                        <button 
                                                            onClick={() => setActiveManualPayment(null)}
                                                            disabled={loadingPayment}
                                                            className="btn bg-white border border-gray-300 text-gray-600 px-2 py-1.5 text-xs hover:bg-gray-50"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* HISTORIQUE DES TRANSACTIONS (OU ANCIENS RDV) */}
                                {((currentApt.transactions && currentApt.transactions.length > 0) || paid > 0) && (
                                    <div className="mt-4 pt-3 border-t border-blue-200/50">
                                        <p className="text-[10px] font-bold text-blue-800 uppercase mb-2">Historique des règlements</p>
                                        <div className="space-y-1.5">
                                            
                                            {/* 1. FALLBACK : ANCIENS RDV SANS HISTORIQUE DÉTAILLÉ */}
                                            {(!currentApt.transactions || currentApt.transactions.length === 0) && paid > 0 && (
                                                <div className="flex justify-between items-center p-2 rounded border text-xs transition-all bg-white/60 border-blue-100">
                                                    <div className="flex items-center gap-2 text-gray-700">
                                                        <span className="font-bold">
                                                            {currentApt.payment_method === 'cash' ? '💶 Espèces' : 
                                                             currentApt.payment_method === 'check' ? '🖋️ Chèque' : 
                                                             currentApt.payment_method === 'stancer' ? '💳 CB En ligne' : 
                                                             '✅ Encaissement validé'}
                                                        </span>
                                                        <span className="text-gray-400">Avant mise à jour</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-bold text-blue-700">
                                                            +{paid.toFixed(2)} €
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 2. AFFICHAGE NORMAL DES NOUVEAUX RÈGLEMENTS */}
                                            {currentApt.transactions && currentApt.transactions.map(t => {
                                                const isNegative = parseFloat(t.amount) < 0;
                                                const isRefunded = t.status === 'refunded';
                                                const isPending = t.status === 'pending';
                                                
                                                return (
                                                    <div key={t.id} className="flex flex-col gap-1">
                                                        {/* Ligne principale de la transaction */}
                                                        <div className={`flex justify-between items-start sm:items-center p-2 rounded border text-xs transition-all ${isNegative ? 'bg-red-50 border-red-100' : t.payment_method === 'discount' ? 'bg-purple-50 border-purple-100' : isPending ? 'bg-yellow-50 border-yellow-200 opacity-80' : 'bg-white/60 border-blue-100'} ${isRefunded ? 'opacity-60 bg-gray-50' : ''}`}>
                                                            <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 ${isRefunded ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                                                                <span className="font-bold whitespace-nowrap">
                                                                    {t.payment_method === 'cash' ? '💶 Espèces' : 
                                                                    t.payment_method === 'check' ? '🖋️ Chèque' : 
                                                                    t.payment_method === 'discount' ? '🎁 Remise' : 
                                                                    t.payment_method === 'card_terminal' ? '💳 CB (TPE)' :
                                                                    t.payment_method === 'transfer' ? '🏦 Virement' :
                                                                    t.payment_method === 'third_party' ? '🏛️ Tiers-Payant' :
                                                                    '🌐 Lien CB'}
                                                                </span>
                                                                <span className="text-gray-400 whitespace-nowrap">• {new Date(t.created_at).toLocaleDateString('fr-FR')}</span>
                                                                {isNegative && <span className="text-[9px] font-bold text-red-600 uppercase border border-red-100 bg-red-50 px-1 rounded whitespace-nowrap">Avoir / Remboursement</span>}
                                                                {isRefunded && <span className="text-[9px] font-bold text-gray-500 uppercase no-underline border border-gray-200 bg-gray-100 px-1 rounded whitespace-nowrap">Remboursé</span>}
                                                                {isPending && (
                                                                    <span className="text-[8px] sm:text-[9px] font-bold text-yellow-700 uppercase no-underline border border-yellow-300 bg-yellow-100 px-1.5 py-0.5 rounded animate-pulse text-center leading-tight">
                                                                        Lien envoyé<br/>(Attente Patient)
                                                                    </span>
                                                                )}
                                                                {t.status === 'forced' && <span className="text-[9px] font-bold text-orange-700 uppercase no-underline border border-orange-300 bg-orange-100 px-1 rounded whitespace-nowrap">Forcé (Attente)</span>}
                                                            </div>
                                                            <div className="flex items-center gap-3 shrink-0 ml-2">
                                                                <span className={`font-bold whitespace-nowrap ${isRefunded ? 'text-gray-400 line-through' : isPending ? 'text-yellow-600' : t.status === 'forced' ? 'text-orange-600' : isNegative ? 'text-red-600' : t.payment_method === 'discount' ? 'text-purple-700' : 'text-blue-700'}`}>
                                                                    {isNegative ? '' : '+'}{t.amount} €
                                                                </span>
                                                                
                                                                {/* Bouton "Forcer" si Pending */}
                                                                {isPending && t.payment_method === 'stancer' && (
                                                                    <button 
                                                                        onClick={() => handleForceTransaction(t.id)}
                                                                        className="p-1 text-orange-500 hover:bg-orange-50 rounded transition-colors ml-1"
                                                                        title="Forcer l'encaissement (Débloque la facture)"
                                                                    >
                                                                        <CheckCircle className="w-4 h-4" />
                                                                    </button>
                                                                )}

                                                                {/* Boutons d'action */}
                                                                {!isNegative && !isRefunded && (
                                                                    <div className="flex items-center gap-1 border-l border-gray-200 pl-2 ml-1">
                                                                        
                                                                        {/* 1. Bouton "Supprimer" (Pour manuels OU liens en attente/forcés) */}
                                                                        {(['cash', 'check', 'discount', 'card_terminal'].includes(t.payment_method) || isPending || t.status === 'forced') && (
                                                                            <button 
                                                                                onClick={() => handleDeleteTransaction(t.id, t.amount, t.payment_method)}
                                                                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                                title={isPending ? "Annuler ce lien" : "Supprimer (Erreur)"}
                                                                            >
                                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        )}
                                                                        
                                                                        {/* 2. Bouton "Rembourser" (Uniquement si VRAIMENT encaissé en ligne ou manuel) */}
                                                                        {t.status === 'paid' && (
                                                                            <button 
                                                                            onClick={() => { 
                                                                                setActiveRefundId(activeRefundId === t.id ? null : t.id); 
                                                                                setRefundAmountInput(t.amount.toString()); 
                                                                            }}
                                                                            className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors text-lg"
                                                                            title="Créer un remboursement / Avoir"
                                                                        >
                                                                            💸
                                                                        </button>)}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Bouton rafraichir si pending */}
                                                                {isPending && (
                                                                    <div className="flex items-center border-l border-yellow-200 pl-2 ml-1">
                                                                         <button 
                                                                            onClick={handleCheckStatus} 
                                                                            className="p-1 text-yellow-600 hover:bg-yellow-100 rounded transition-colors" 
                                                                            title="Vérifier si le patient a payé"
                                                                        >
                                                                            <RefreshCw className={`w-3.5 h-3.5 ${loadingPayment ? 'animate-spin' : ''}`} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* NOUVEAU : AFFICHER LE LIEN CB S'IL EST EN ATTENTE */}
                                                        {isPending && t.payment_link && (
                                                            <div className="mt-1 flex items-center gap-2 bg-yellow-50/50 p-1.5 rounded border border-yellow-200/50 ml-4 animate-in slide-in-from-top-1">
                                                                <input 
                                                                    type="text" 
                                                                    readOnly 
                                                                    value={t.payment_link} 
                                                                    onClick={(e) => e.target.select()}
                                                                    title="Lien de paiement"
                                                                    className="flex-1 text-[10px] bg-transparent border-none p-0 text-gray-500 font-mono focus:ring-0 truncate cursor-text"
                                                                />
                                                                <a 
                                                                    href={t.payment_link} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="btn bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 py-1 px-2 text-[10px] shadow-sm flex items-center gap-1 shrink-0"
                                                                    title="Ouvrir la page de paiement dans un nouvel onglet"
                                                                >
                                                                    <ExternalLink className="w-3 h-3" /> Ouvrir
                                                                </a>
                                                                <button 
                                                                    onClick={() => {
                                                                        if (navigator.clipboard && window.isSecureContext) {
                                                                            navigator.clipboard.writeText(t.payment_link)
                                                                        } else {
                                                                            const ta = document.createElement("textarea")
                                                                            ta.value = t.payment_link
                                                                            document.body.appendChild(ta)
                                                                            ta.select()
                                                                            document.execCommand("copy")
                                                                            ta.remove()
                                                                        }
                                                                        toast.success("Lien copié !")
                                                                    }}
                                                                    className="btn bg-white border border-yellow-200 text-yellow-700 hover:bg-yellow-100 py-1 px-2 text-[10px] shadow-sm flex items-center gap-1 shrink-0"
                                                                >
                                                                    <Copy className="w-3 h-3" /> Copier
                                                                </button>
                                                            </div>
                                                        )}


                                                        {/* Ligne dépliante pour saisir le remboursement */}
                                                        {activeRefundId === t.id && (
                                                            <div className="bg-orange-50 border border-orange-200 p-2 rounded-lg animate-in fade-in zoom-in-95 flex items-center gap-2 ml-4">
                                                                <label className="text-[10px] font-bold text-orange-800 uppercase">Rembourser :</label>
                                                                <div className="relative flex-1 max-w-[120px]">
                                                                    <input 
                                                                        type="number" 
                                                                        value={refundAmountInput}
                                                                        onChange={e => setRefundAmountInput(e.target.value)}
                                                                        max={t.amount}
                                                                        className="w-full text-xs py-1.5 pr-6 rounded border-orange-200 focus:ring-orange-300 font-bold text-orange-900"
                                                                    />
                                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-orange-800">€</span>
                                                                </div>
                                                                <button 
                                                                    onClick={() => handleRefundTransaction(t.id, t.amount, t.payment_method)}
                                                                    disabled={loadingPayment}
                                                                    className="btn bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 text-xs shadow-sm"
                                                                >
                                                                    Valider
                                                                </button>
                                                                <button 
                                                                    onClick={() => { setActiveRefundId(null); setRefundAmountInput(''); }}
                                                                    disabled={loadingPayment}
                                                                    className="btn bg-white border border-gray-300 text-gray-600 px-2 py-1.5 text-xs hover:bg-gray-50"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* E. FACTURE (Uniquement si le solde est à 0) */}
                                {isFullyPaid && (
                                    <div className="mt-4 pt-4 border-t border-blue-200/50 flex gap-2">
                                        <button 
                                            onClick={handlePreviewInvoice}
                                            className="flex-1 btn bg-white border border-sage-300 text-sage-700 hover:bg-sage-50 flex items-center justify-center gap-2 py-2"
                                            title="Aperçu (Nouvel onglet)"
                                        >
                                            <SearchIcon className="w-4 h-4" /> 
                                            {currentApt.invoice_path ? "Aperçu" : "Générer & Voir"}
                                        </button>
                                        {currentApt.invoice_path && (
                                            <button 
                                                onClick={handleDownloadInvoice}
                                                className="flex-1 btn bg-sage-600 text-white hover:bg-sage-700 flex items-center justify-center gap-2 py-2"
                                                title="Télécharger"
                                            >
                                                <Download className="w-4 h-4" /> Télécharger
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })()}

                    {/* Patient Infos & CONTACT RAPIDE */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 text-gray-700">
                            <div className="w-8 h-8 rounded-full bg-sage-100 flex items-center justify-center">
                                <Mail className="w-4 h-4 text-sage-600" />
                            </div>
                            <a 
                                href={`mailto:${currentApt.patient_email}`}
                                className="hover:text-sage-600 hover:underline transition-colors"
                            >
                                {currentApt.patient_email}
                            </a>
                        </div>
                        <div className="flex items-center gap-3 text-gray-700">
                            <div className="w-8 h-8 rounded-full bg-sage-100 flex items-center justify-center">
                                <Phone className="w-4 h-4 text-sage-600" />
                            </div>
                            <span className="font-mono">{currentApt.patient_phone}</span>
                        </div>

                        {/* SECTION SMS RAPIDE */}
                        <div className="mt-2 pt-3 border-t border-gray-100">
                            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Envoyer un SMS rapide</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Message pour le patient..."
                                    value={smsMessage}
                                    onChange={e => setSmsMessage(e.target.value)}
                                    className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-sage-200"
                                    onKeyDown={e => {
                                        if(e.key === 'Enter') handleSendSms()
                                    }}
                                />
                                <button
                                    onClick={handleSendSms}
                                    disabled={sendingSms || !smsMessage.trim()}
                                    className="btn btn-primary px-3 py-2 rounded-lg flex items-center justify-center disabled:opacity-50"
                                    title="Envoyer"
                                >
                                    {sendingSms ? <div className="spinner w-4 h-4" /> : <Send className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Motif & Notes (AVEC STYLE POST-IT) */}
                    <div className="space-y-3">
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Motif</p>
                            <p className="text-gray-800 font-medium">{currentApt.reason_name || "Non spécifié"}</p>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 shadow-sm relative">
                            <div className="absolute top-0 right-0 p-2 opacity-20">
                                <StickyNote className="w-8 h-8 text-yellow-600" />
                            </div>
                            <p className="text-xs font-bold text-yellow-700 uppercase mb-2">Notes internes</p>
                            {currentApt.notes ? (
                                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                                    {currentApt.notes}
                                </p>
                            ) : (
                                <p className="text-sm text-gray-400 italic">Aucune note pour ce rendez-vous.</p>
                            )}
                        </div>
                    </div>

                    {/* SECTION VIDEO */}
                    {currentApt.consultation_type === 'video' && (
                        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mt-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white p-2 rounded-full shadow-sm">
                                        <Video className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-purple-900 text-sm">Consultation Vidéo</p>
                                        <p className="text-xs text-purple-600">Le patient a reçu ce lien.</p>
                                    </div>
                                </div>
                                
                                {/* BOUTON D'ENVOI MANUEL */}
                                <button
                                    onClick={handleResendVideo}
                                    disabled={resendingVideo}
                                    className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors border border-purple-200 bg-white shadow-sm"
                                    title="Renvoyer le lien par Email/SMS"
                                >
                                    {resendingVideo ? <div className="spinner w-4 h-4 border-purple-600"/> : <Send className="w-4 h-4"/>}
                                </button>
                            </div>
                            <a
                                href={`https://meet.jit.si/BellaBlue-${currentApt.token}?lang=fr#config.disableDeepLinking=true`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn bg-purple-600 text-white hover:bg-purple-700 w-full flex justify-center items-center gap-2 py-2 shadow-sm transition-all hover:scale-[1.02]"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Rejoindre la salle d'attente
                            </a>
                        </div>
                    )}

                    {/* Actions */}
                    {/* GROS BOUTON DEMARRER CONSULTATION */}
                    <div className="pt-4 border-t border-gray-100">
                        <button 
                            onClick={() => navigate(`/admin/patients/${currentApt.patient_id}?action=new_note&apt_id=${currentApt.id}`)}
                            className="w-full btn bg-sage-600 text-white hover:bg-sage-700 flex justify-center items-center gap-2 py-3 shadow-md transition-all hover:scale-[1.02]"
                        >
                            <FileText className="w-5 h-5" /> Démarrer la consultation
                        </button>
                    </div>

                    {/* Actions secondaires */}
                    <div className="grid grid-cols-2 gap-3 pt-4">
                        <button onClick={() => handleEditClick(currentApt)} className="btn bg-white border border-sage-200 text-sage-700 hover:bg-sage-50 flex justify-center items-center gap-2 py-2.5">
                            <Save className="w-4 h-4" /> Modifier
                        </button>
                        {currentApt.status !== 'cancelled' ? (
                            <button onClick={() => handleStatusChange(currentApt.id, 'cancelled')} className="btn bg-white border border-red-200 text-red-600 hover:bg-red-50 flex justify-center items-center gap-2 py-2.5">
                                <XCircle className="w-4 h-4" /> Annuler
                            </button>
                        ) : (
                            <button onClick={() => handleStatusChange(currentApt.id, 'confirmed')} className="btn bg-green-600 text-white hover:bg-green-700 flex justify-center items-center gap-2 py-2.5">
                                <CheckCircle className="w-4 h-4" /> Réactiver
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* MODALE CRÉATION / ÉDITION */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="bg-sage-600 p-4 text-white flex justify-between items-center sticky top-0 z-10">
              <h3 className="font-bold flex items-center gap-2">
                <UserPlus className="w-5 h-5" /> 
                {isEditing ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
              </h3>
              <button onClick={() => setCreateModalOpen(false)}><X className="w-6 h-6 text-white" /></button>
            </div>
            
            <div className="p-6 space-y-4">
              
              {!isEditing && (
                <div className="relative">
                    <label className="text-[10px] font-bold text-gray-400 uppercase">Rechercher un patient</label>
                    <div className="relative mt-1">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                          type="text" 
                          placeholder="Nom ou téléphone..." 
                          value={patientSearch} 
                          onChange={(e) => { setPatientSearch(e.target.value); if (selectedPatientId) setSelectedPatientId(null); }} 
                          className="w-full pl-9 pr-4 py-2 bg-gray-50 border-gray-200 rounded-xl focus:ring-sage-500" 
                        />
                    </div>
                    {filteredPatientsSearch.length > 0 && !selectedPatientId && (
                        <div className="absolute z-10 w-full bg-white border border-gray-100 shadow-xl rounded-xl mt-1 overflow-hidden">
                            {filteredPatientsSearch.map(p => (
                                <button key={p.id} onClick={() => selectExistingPatient(p)} className="w-full text-left px-4 py-3 hover:bg-sage-50 flex justify-between items-center border-b border-gray-50 last:border-none">
                                    <div>
                                      <p className="font-bold text-gray-800 text-sm">{p.first_name} {p.last_name}</p>
                                      <p className="text-xs text-gray-500">{p.phone}</p>
                                    </div>
                                    <div className="bg-sage-100 text-sage-600 p-1 rounded-full"><Check className="w-3 h-3"/></div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
              )}

              {/* TYPE DE PATIENT (Nouveau / Suivi) */}
              <div className="bg-sage-50 p-3 rounded-xl mb-3 flex items-center justify-between">
                <span className="text-xs font-bold text-sage-700 uppercase">Type de consultation</span>
                <div className="flex bg-white rounded-lg p-1 border border-sage-200">
                    <button 
                        onClick={() => setNewAptData(prev => ({...prev, patient_type: 'new', tariff_id: '', price: sysSettings.price_new, custom_duration: ''}))}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                            newAptData.patient_type === 'new' 
                            ? 'bg-sage-600 text-white shadow-sm' 
                            : 'text-sage-600 hover:bg-sage-50'
                        }`}
                    >
                        Nouveau ({sysSettings.duration_new} min)
                    </button>
                    <button 
                        onClick={() => setNewAptData(prev => ({...prev, patient_type: 'follow_up', tariff_id: '', price: sysSettings.price_followup, custom_duration: ''}))}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                            newAptData.patient_type === 'follow_up' 
                            ? 'bg-sage-600 text-white shadow-sm' 
                            : 'text-sage-600 hover:bg-sage-50'
                        }`}
                    >
                        Suivi ({sysSettings.duration_followup} min)
                    </button>

                </div>
              </div>

              {/* Infos Patient (sécurisation des onChange) */}
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Prénom" value={newAptData.first_name} onChange={e => setNewAptData(prev => ({...prev, first_name: e.target.value}))} className="text-sm rounded-xl border-gray-200" />
                <input placeholder="Nom" value={newAptData.last_name} onChange={e => setNewAptData(prev => ({...prev, last_name: e.target.value}))} className="text-sm rounded-xl border-gray-200" />
              </div>
              <input placeholder="Email" value={newAptData.email} onChange={e => setNewAptData(prev => ({...prev, email: e.target.value}))} className="w-full text-sm rounded-xl border-gray-200" />
              <input placeholder="Téléphone" value={newAptData.phone} onChange={e => setNewAptData(prev => ({...prev, phone: e.target.value}))} className="w-full text-sm rounded-xl border-gray-200" />
              
              <div className="border-t border-gray-100 pt-2"></div>


              {/* DATE - Lieu - Tarif */}
              <div className="grid grid-cols-2 gap-3">
                {/* DATE ET HEURE */}
                {/* ... Input Date inchangé ... */}
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase">Date</label>
                  <input 
                    type="date" 
                    value={newAptData.date} 
                    onChange={e => setNewAptData(prev => ({...prev, date: e.target.value}))} 
                    className="w-full text-sm rounded-xl border-gray-200" 
                  />
                </div>
                {/* ... Select Lieu inchangé ... */}
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase">Lieu</label>
                  <select value={newAptData.consultation_type} onChange={e => setNewAptData(prev => ({...prev, consultation_type: e.target.value}))} className="w-full text-sm rounded-xl border-gray-200">
                    <option value="cabinet">Cabinet</option>
                    <option value="video">Vidéo</option>
                    <option value="domicile">Domicile</option>
                    <option value="telephone">Téléphone</option>
                  </select>
                </div>
              </div>

              {/* SUGGESTIONS DE CRÉNEAUX & INPUT HEURE */}
              <div>
                 <div className="relative flex justify-between items-center mb-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase ">
                        Heure du RDV
                        {loadingSlots && <span className="text-sage-600 flex items-center gap-1 font-normal lowercase"><Loader2 className="w-3 h-3 animate-spin"/> calcul...</span>}
                        {!loadingSlots && slotSuggestions.length > 0 && <span className="text-sage-600 font-normal normal-case">Suggestions disponibles</span>}
                    </label>
                 </div>
                 
                 {/* Liste des suggestions cliquables (inchangée) */}
                 {newAptData.date && slotSuggestions.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-thin">
                        {slotSuggestions.map((slot, idx) => (
                            <button 
                                key={idx}
                                onClick={() => setNewAptData(prev => ({ ...prev, start_time: slot.start_time.slice(0, 5) }))}
                                className={`px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap transition-colors border ${
                                    newAptData.start_time === slot.start_time.slice(0, 5)
                                    ? 'bg-sage-600 text-white border-sage-600'
                                    : 'bg-sage-50 text-sage-700 border-sage-200 hover:bg-sage-100'
                                }`}
                            >
                                {slot.start_time.slice(0, 5)}
                            </button>
                        ))}
                    </div>
                 )}

                 {/* INPUT HEURE AVEC CORRECTION CSS ET FEEDBACK FIN */}
                 <div className="flex items-center gap-2">
                     
                     <div className="relative flex-1">
                        <input 
                            type="time" 
                            value={newAptData.start_time} 
                            onChange={e => setNewAptData(prev => ({...prev, start_time: e.target.value}))} 
                            className={`w-full text-sm rounded-xl border-gray-200 font-bold pr-12 ${conflictWarning ? 'border-orange-300 ring-2 ring-orange-100' : ''}`} 
                        />
                        {!conflictWarning && newAptData.start_time && (
                             <div className="absolute right-8 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none">
                                <CheckCircle className="w-4 h-4" />
                             </div>
                        )}
                     </div>
                     
                     {/* SÉLECTEUR DE DURÉE PERSONNALISÉE */}
                     <div className="w-28">
                        {(() => {
                            const currentDur = newAptData.custom_duration || (newAptData.patient_type === 'new' ? (sysSettings.duration_new || 60) : (sysSettings.duration_followup || 30));
                            const standardOptions = [15, 20, 30, 45, 60, 75, 90, 120]; // Liste des standards
                            return (
                                <select 
                                    value={currentDur} 
                                    onChange={e => setNewAptData(prev => ({...prev, custom_duration: parseInt(e.target.value)}))}
                                    className="w-full text-sm rounded-xl border-gray-200 bg-white font-medium text-sage-700"
                                >
                                    {/* On affiche la valeur actuelle en haut UNIQUEMENT si elle n'est pas standard */}
                                    {!standardOptions.includes(currentDur) && (
                                        <>
                                            <option value={currentDur}>{currentDur} min</option>
                                            <option disabled>──────</option>
                                        </>
                                    )}
                                    <option value={15}>15 min</option>
                                    <option value={20}>20 min</option>
                                    <option value={30}>30 min</option>
                                    <option value={45}>45 min</option>
                                    <option value={60}>1 heure</option>
                                    <option value={75}>1h15</option>
                                    <option value={90}>1h30</option>
                                    <option value={120}>2 heures</option>
                                </select>
                            );
                        })()}
                     </div>



                     {/* AFFICHAGE DE L'HEURE DE FIN CALCULÉE */}
                     {newAptData.start_time && (
                        <div className="bg-gray-100 px-3 py-2 rounded-xl text-xs font-medium text-gray-600 whitespace-nowrap">
                            ➔ {(() => {
                                const [h, m] = newAptData.start_time.split(':').map(Number);
                                const duration = newAptData.custom_duration || (newAptData.patient_type === 'new' ? (sysSettings.duration_new || 60) : (sysSettings.duration_followup || 30));
                                    
                                const end = new Date();
                                end.setHours(h, m + duration);
                                return end.toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'});
                            })()}
                        </div>
                     )}
                 </div>
                 
                 {/* RÉSEAUX ET TIERS PAYANT */}
                 <div className="grid grid-cols-2 gap-3 mt-3 bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                    <div>
                        <label className="text-[10px] font-bold text-blue-800 uppercase">Tarif Conventionné / Réseau</label>
                        <select 
                            value={newAptData.tariff_id || ''} 
                            onChange={e => {
                                const tId = e.target.value;
                                const selected = tariffs.find(t => t.id == tId);
                                setNewAptData(prev => ({
                                    ...prev, 
                                    tariff_id: tId, 
                                    price: selected ? selected.amount : (prev.patient_type === 'new' ? sysSettings.price_new : sysSettings.price_followup)
                                }))
                            }} 
                            className="w-full text-sm rounded-xl border-blue-200 bg-white mt-1"
                        >
                            <option value="">Tarif Classique du Cabinet</option>
                            {/* ON FILTRE SELON LE TYPE DE PATIENT */}
                            {tariffs.filter(t => t.patient_type === 'all' || t.patient_type === newAptData.patient_type).map(t => (
                                <option key={t.id} value={t.id}>{t.name} ({t.amount}€)</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-blue-800 uppercase">Tiers-Payant (Association)</label>
                        <select 
                            value={newAptData.third_party_payer_id || ''} 
                            onChange={e => setNewAptData(prev => ({...prev, third_party_payer_id: e.target.value}))} 
                            className="w-full text-sm rounded-xl border-blue-200 bg-white mt-1"
                        >
                            <option value="">Aucun (Patient paie 100%)</option>
                            {tpps.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                 </div>
              </div>

              {/* ALERTE ANTI-TRICHE (Si le prix saisi ne correspond pas au Tarif Réseau) */}
              {(() => {
                  const selectedTariffObj = tariffs.find(t => t.id == newAptData.tariff_id);
                  const isPriceOverridden = selectedTariffObj && parseFloat(newAptData.price) !== parseFloat(selectedTariffObj.amount);
                  
                  if (isPriceOverridden) {
                      return (
                          <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 flex items-start gap-3 mt-3 animate-in fade-in slide-in-from-top-2">
                              <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                              <div>
                                  <p className="text-xs font-bold text-yellow-800">Avertissement : Prix Modifié</p>
                                  <p className="text-[10px] text-yellow-700 leading-tight mt-0.5">
                                      Le montant saisi ({newAptData.price}€) diffère du tarif réseau officiel "{selectedTariffObj.name}" ({selectedTariffObj.amount}€). 
                                      <br/><strong>Le Tiers-Payant s'appliquera sur le montant modifié.</strong> 
                                      <br/>Si vous combinez plusieurs actes, merci de créer des rendez-vous séparés pour une facturation conforme.
                                  </p>
                              </div>
                          </div>
                      );
                  }
                  return null;
              })()}

              {/* ENCART PRIX BIEN VISIBLE */}
              <div className="bg-sage-50 border-2 border-sage-200 p-3 rounded-xl flex items-center justify-between shadow-sm mt-3">
                  <div>
                      <label className="text-[10px] font-bold text-sage-800 uppercase block">Montant facturé</label>
                      <p className="text-[10px] text-sage-600 italic">Modifiable manuellement</p>
                  </div>
                  <div className="relative w-32">
                      <input 
                          type="number" 
                          step="1.00"
                          value={newAptData.price || ''} 
                          onChange={e => setNewAptData(prev => ({...prev, price: e.target.value}))} 
                          className="w-full text-lg font-black text-sage-800 bg-white border-sage-300 rounded-xl pr-8 text-right focus:ring-sage-500" 
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-600 font-bold">€</span>
                  </div>
              </div>

              {/* ALERTE CONFLIT */}
              {conflictWarning && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0" />
                    <div>
                        <p className="text-sm font-bold text-orange-800">Attention</p>
                        <p className="text-xs text-orange-700">{conflictWarning}</p>
                    </div>
                </div>
              )}

              {/* MOTIF ET NOTES (Correction écrasement) */}
              <div className="space-y-3">
                <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase">Motif</label>
                    <select value={newAptData.reason_id} onChange={e => setNewAptData(prev => ({...prev, reason_id: e.target.value}))} className="w-full text-sm rounded-xl border-gray-200">
                        <option value="">Sélectionnez un motif...</option>
                        {reasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase">Notes internes</label>
                    <textarea 
                        rows={3}
                        value={newAptData.notes} 
                        onChange={e => {
                            const val = e.target.value;
                            setNewAptData(prev => ({...prev, notes: val}))
                        }} 
                        placeholder="Infos importantes sur ce patient..."
                        className="w-full text-sm rounded-xl border-gray-200 bg-yellow-50 focus:bg-white transition-colors"
                    />
                </div>
              </div>

              {/* SILENT UPDATE CHECKBOX */}
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer mt-4 mb-4 bg-gray-50 p-3 rounded-xl border border-gray-100 transition-colors hover:bg-gray-100">
                  <input 
                      type="checkbox" 
                      checked={newAptData.silent_update} 
                      onChange={e => setNewAptData(prev => ({...prev, silent_update: e.target.checked}))} 
                      className="w-4 h-4 text-sage-600 rounded focus:ring-sage-500"
                  />
                  Ne pas notifier le patient par SMS/Email pour cette action
              </label>

              {/* SAVE BUTTON */}
              <button 
                onClick={handleAdminSubmitApt} 
                disabled={loading} 
                className={`w-full btn py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-colors ${
                    conflictWarning 
                    ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                    : 'btn-primary'
                }`}
              >
                {loading ? <div className="spinner w-5 h-5" /> : (
                    <>
                        <Save className="w-5 h-5" /> 
                        {conflictWarning ? 'Forcer la sauvegarde' : (isEditing ? 'Sauvegarder' : 'Créer le rendez-vous')}
                    </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}